import create from "zustand";
import { Patient } from "../types";
import { bookmarkNews } from "../pages/tatvaShots/service";

interface DoctorDetails {
  id: string;
  name: string;
  specialty: string;
  mobileNumber: string;
}

interface Condition {
  medical_condition_group_id: string;
  medical_condition_name: string;
}

interface CarePlan {
  plan_master_id: string;
  plan_name: string;
}

interface LikesState {
  likedShots: Record<string, boolean>;
  likeCounts: Record<string, number>;
  pendingLikes: Record<string, boolean>;
}

interface LikesActions {
  updateLikeStatus: (shotId: string, isLiked: boolean) => Promise<void>;
  initializeLikes: (shots: any[]) => void;
  resetLikes: () => void;
}

interface BookmarkState {
  bookmarkedShots: Record<string, boolean>;
  pendingBookmarks: Record<string, boolean>;
}

interface BookmarkActions {
  updateBookmarkStatus: (
    shotId: string,
    isBookmarked: boolean
  ) => Promise<void>;
  initializeBookmarks: (shots: any[]) => void;
  resetBookmarks: () => void;
}

interface SpecialityItem {
  id: number;
  code: string;
  displayName: string;
  pmMasterId?: number;
  pmEnabled: boolean;
}

interface ContinuumItem {
  id: number;
  code: string;
  depends?: string;
  pmEnabled: boolean;
}

interface ContentGenreItem {
  id: number;
  code: string;
  depends: string;
  pmEnabled: boolean;
}

interface ContentTypeItem {
  id: number;
  code: string;
  sequence: number;
}

interface TatvaPediaFilters {
  "Content Type": ContentTypeItem[];
  "Speciality": SpecialityItem[];
  "Genre": ContentGenreItem[];
  "Continuum": ContinuumItem[];
  "Interestpreference": ContentTypeItem[];
  "ConsolidatedModule": ContentTypeItem[];
}

interface SelectedFilters {
  "Content Type": Array<{
    id: string | number;
    name: string;
  }>;
  "Genre": Array<{
    id: string | number;
    name: string;
    depends?: string;
  }>;
  "Continuum": Array<{
    id: string | number;
    name: string;
    depends?: string;
  }>;
  "Speciality": Array<{
    id: string | number;
    name: string;
  }>;
  Interestpreference: Array<{
    id: string | number;
    name: string;
  }>;
  ConsolidatedModule: Array<{
    id: string | number;
    name: string;
  }>;
}

interface TatvaPediaState {
  bookmarkedList: any[];
  likedList: any[];
}

interface TatvaPediaActions {
  updateTatvaPediaBookmark: (content: any, isBookmarked: boolean) => void;
  setTatvaPediaBookmarkedList: (list: any[]) => void;
  updateTatvaPediaLike: (content: any, isLiked: boolean) => void;
  setTatvaPediaLikedList: (list: any[]) => void;
}

interface State extends LikesState, BookmarkState {
  doctorDetails: DoctorDetails | null | any;
  condition: Condition | null;
  carePlan: CarePlan | null;
  patientName: string;
  mobileNumber: string;
  setDoctorDetails: (details: DoctorDetails) => void;
  setCondition: (condition: Condition) => void;
  setCarePlan: (carePlan: CarePlan) => void;
  setPatientName: (name: string) => void;
  setMobileNumber: (number: string) => void;
  resetPatientPlan: () => void;
  referredPatients: Patient[];
  setReferredPatients: (patient: Patient[]) => void;
  totalPatientsReferred: number;
  totalplanPurchased: number;
  setTotalPatientsReferred: (totalPatientsReferred: number) => void;
  setTotalplanPurchased: (totalplanPurchased: number) => void;
  tatvaShots: {
    myFeed: any[];
    trending: any[];
    allNews: any[];
    bookmarks: any[];
    userPreferences: any[];
    categories: any[];
    topics: any[];
    topicNews: any[];
    shotTags: any[];
  };
  setTatvaShots: (shots: any) => void;
  planDetails: any;
  setPlanDetails: (planDetails: any) => void;
  updateLikeStatus: LikesActions["updateLikeStatus"];
  initializeLikes: LikesActions["initializeLikes"];
  resetLikes: LikesActions["resetLikes"];
  updateBookmarkStatus: BookmarkActions["updateBookmarkStatus"];
  initializeBookmarks: BookmarkActions["initializeBookmarks"];
  resetBookmarks: BookmarkActions["resetBookmarks"];
  ddxHistoryCount: number | null;
  setDDxHistoryCount: (count: number) => void;
  tatvaPediaFilters: TatvaPediaFilters;
  setTatvaPediaFilters: (filters: TatvaPediaFilters) => void;
  resetTatvaPediaFilters: () => void;
  tatvaPediaToken: any;
  setTatvaPediaToken: (token: string) => void;
  selectedTatvaPediaFilters: SelectedFilters;
  setSelectedTatvaPediaFilters: (filters: SelectedFilters) => void;
  resetSelectedTatvaPediaFilters: () => void;
  tatvaPediaBookmarks: TatvaPediaState;
  updateTatvaPediaBookmark: TatvaPediaActions["updateTatvaPediaBookmark"];
  setTatvaPediaBookmarkedList: TatvaPediaActions["setTatvaPediaBookmarkedList"];
  setTatvaPediaLikedList: TatvaPediaActions["setTatvaPediaLikedList"];
  updateTatvaPediaLike: TatvaPediaActions["updateTatvaPediaLike"];
  pediaUserPreferences: any;
  setPediaUserPreferences: (preferences: any) => void;
}

const initialState = {
  condition: null,
  carePlan: null,
  patientName: "",
  mobileNumber: "",
};

const initialTatvaPediaFilters: TatvaPediaFilters = {
  "Content Type": [],
  "Speciality": [],
  "Genre": [],
  "Continuum": [],
  "Interestpreference": [],
  "ConsolidatedModule": [],
};

const initialSelectedFilters: SelectedFilters = {
  "Content Type": [],
  "Speciality": [],
  "Genre": [],
  "Continuum": [],
  Interestpreference: [],
  ConsolidatedModule: [],
};

// Add these helper functions to handle localStorage
const getStoredBookmarks = () => {
  const stored = localStorage.getItem('tatvaPediaBookmarks');
  return stored ? JSON.parse(stored) : { likedList: [], bookmarkedList: [] };
};

const useStore = create<State>((set) => ({
  ddxHistoryCount: null,
  setDDxHistoryCount: (count) => set({ ddxHistoryCount: count }),
  doctorDetails: null,
  referredPatients: [],
  condition: null,
  carePlan: null,
  patientName: "",
  mobileNumber: "",
  totalPatientsReferred: 0,
  totalplanPurchased: 0,
  planDetails: null,
  likedShots: {},
  likeCounts: {},
  pendingLikes: {},
  bookmarkedShots: {},
  pendingBookmarks: {},
  setDoctorDetails: (doctorDetails) => set({ doctorDetails }),
  setCondition: (condition) => set({ condition }),
  setCarePlan: (carePlan) => set({ carePlan }),
  setPatientName: (patientName) => set({ patientName }),
  setMobileNumber: (mobileNumber) => set({ mobileNumber }),
  resetPatientPlan: () => set(initialState),
  setReferredPatients: (referredPatients) => set({ referredPatients }),
  setTotalPatientsReferred: (totalPatientsReferred) =>
    set({ totalPatientsReferred }),
  setTotalplanPurchased: (totalplanPurchased) => set({ totalplanPurchased }),
  tatvaShots: {
    myFeed: [],
    trending: [],
    allNews: [],
    bookmarks: [],
    userPreferences: [],
    categories: [],
    topics: [],
    topicNews: [],
    shotTags: [],
  },
  setTatvaShots: (shots) =>
    set((state) => ({ tatvaShots: { ...state.tatvaShots, ...shots } })),
  setPlanDetails: (planDetails) => set({ planDetails }),
  updateLikeStatus: async (shotId: string, isLiked: boolean) => {
    const state = useStore.getState();

    // Don't proceed if there's a pending request
    if (state.pendingLikes[shotId]) return;

    // Save previous state
    const previousState = {
      likedShots: { ...state.likedShots },
      likeCounts: { ...state.likeCounts },
    };

    try {
      // Set pending state
      set({ pendingLikes: { ...state.pendingLikes, [shotId]: true } });

      // Optimistic update
      set({
        likedShots: { ...state.likedShots, [shotId]: isLiked },
        likeCounts: {
          ...state.likeCounts,
          [shotId]: (state.likeCounts[shotId] || 0) + (isLiked ? 1 : -1),
        },
      });

      // Make API call to your bookmarkNews service
      const res = await bookmarkNews({
        newsId: shotId,
        isLiked: isLiked,
      });

      if (res?.status !== 200) {
        throw new Error("Failed to update like status");
      }
    } catch (error) {
      // Revert to previous state on error
      set({
        likedShots: previousState.likedShots,
        likeCounts: previousState.likeCounts,
      });
      throw error;
    } finally {
      // Clear pending state
      set((state) => ({
        pendingLikes: { ...state.pendingLikes, [shotId]: false },
      }));
    }
  },

  initializeLikes: (shots) => {
    const likeCounts: Record<string, number> = {};
    const likedShots: Record<string, boolean> = {};

    shots.forEach((shot: any) => {
      if (shot.id) {
        likeCounts[shot.id] = shot.likes || 0;
        likedShots[shot.id] = shot.isLiked || false;
      }
    });

    set({ likeCounts, likedShots });
  },

  resetLikes: () => {
    set({
      likedShots: {},
      likeCounts: {},
      pendingLikes: {},
    });
  },
  updateBookmarkStatus: async (shotId: string, isBookmarked: boolean) => {
    const state = useStore.getState();

    // Don't proceed if there's a pending request
    if (state.pendingBookmarks[shotId]) return;

    // Save previous state
    const previousBookmarkState = {
      bookmarkedShots: { ...state.bookmarkedShots },
    };

    try {
      // Set pending state
      set({ pendingBookmarks: { ...state.pendingBookmarks, [shotId]: true } });

      // Optimistic update
      set({
        bookmarkedShots: { ...state.bookmarkedShots, [shotId]: isBookmarked },
      });

      // Make API call to your bookmarkNews service
      const res = await bookmarkNews({
        newsId: shotId,
        isBookmarked: isBookmarked,
      });

      if (res?.status !== 200) {
        throw new Error("Failed to update bookmark status");
      }
    } catch (error) {
      // Revert to previous state on error
      set(() => ({
        bookmarkedShots: previousBookmarkState.bookmarkedShots,
      }));
      throw error;
    } finally {
      // Clear pending state
      set((state) => ({
        pendingBookmarks: { ...state.pendingBookmarks, [shotId]: false },
      }));
    }
  },

  initializeBookmarks: (shots) => {
    const bookmarkedShots: Record<string, boolean> = {};

    shots.forEach((shot: any) => {
      if (shot.id) {
        bookmarkedShots[shot.id] = shot.isBookmarked || false;
      }
    });

    set({ bookmarkedShots });
  },

  resetBookmarks: () => {
    set({
      bookmarkedShots: {},
      pendingBookmarks: {},
    });
  },

  // Add TatvaPedia filter state and actions
  tatvaPediaFilters: initialTatvaPediaFilters,
  
  setTatvaPediaFilters: (filters) => 
    set({ tatvaPediaFilters: filters }),
  
  resetTatvaPediaFilters: () => 
    set({ tatvaPediaFilters: initialTatvaPediaFilters }),

  // Add TatvaPedia token state and actions
  tatvaPediaToken: null,
  setTatvaPediaToken: (token) => set({ tatvaPediaToken: token }),

  // Add selected filters state and actions
  selectedTatvaPediaFilters: initialSelectedFilters,
  setSelectedTatvaPediaFilters: (filters) => 
    set({ selectedTatvaPediaFilters: filters }),
  resetSelectedTatvaPediaFilters: () =>
    set({ selectedTatvaPediaFilters: initialSelectedFilters }),

  tatvaPediaBookmarks: getStoredBookmarks(),
  
  // Update the setters to also update localStorage
  setTatvaPediaBookmarkedList: (bookmarkedList: any[]) => {
    set((state) => {
      const newBookmarks = {
        ...state.tatvaPediaBookmarks,
        bookmarkedList
      };
      localStorage.setItem('tatvaPediaBookmarks', JSON.stringify(newBookmarks));
      return { tatvaPediaBookmarks: newBookmarks };
    });
  },

  setTatvaPediaLikedList: (likedList: any[]) => {
    set((state) => {
      const newBookmarks = {
        ...state.tatvaPediaBookmarks,
        likedList
      };
      localStorage.setItem('tatvaPediaBookmarks', JSON.stringify(newBookmarks));
      return { tatvaPediaBookmarks: newBookmarks };
    });
  },

  updateTatvaPediaLike: (content: any, isLiked: boolean) => {
    set((state) => {
      let newLikedList;
      if (isLiked) {
        newLikedList = [...(state.tatvaPediaBookmarks?.likedList || []), content];
      } else {
        newLikedList = (state.tatvaPediaBookmarks?.likedList || [])
          .filter((item: any) => item.contentId !== content.contentId);
      }

      const newBookmarks = {
        ...state.tatvaPediaBookmarks,
        likedList: newLikedList
      };
      localStorage.setItem('tatvaPediaBookmarks', JSON.stringify(newBookmarks));
      return { tatvaPediaBookmarks: newBookmarks };
    });
  },

  updateTatvaPediaBookmark: (content: any, isBookmarked: boolean) => {
    set((state) => {
      let newBookmarkedList;
      if (isBookmarked) {
        newBookmarkedList = [...(state.tatvaPediaBookmarks?.bookmarkedList || []), content];
      } else {
        newBookmarkedList = (state.tatvaPediaBookmarks?.bookmarkedList || [])
          .filter((item: any) => item.contentId !== content.contentId);
      }

      const newBookmarks = {
        ...state.tatvaPediaBookmarks,
        bookmarkedList: newBookmarkedList
      };
      localStorage.setItem('tatvaPediaBookmarks', JSON.stringify(newBookmarks));
      return { tatvaPediaBookmarks: newBookmarks };
    });
  },

  pediaUserPreferences: null,
  setPediaUserPreferences: (preferences) =>
    set({ pediaUserPreferences: preferences }),
}));

export default useStore;
